import {
  BUYER_DEAL_STAGE_LABELS,
  SELLER_DEAL_STAGE_LABELS,
} from "@repo/database/src/deal-stages";

/** Same split the buyers board uses: inquiry, advised client, or principal. */
export type DealCardBuyerKind =
  | "listing_inquiry"
  | "buyer_client"
  | "principal_buyer";

export type DealCardListingShape = {
  title?: string | null;
  pitch?: string | null;
  askingPrice?: number | bigint | null;
  listingMedia?: Array<{
    image?: { file?: { key?: string | null } | null } | null;
  }> | null;
  placeholderImage?: { file?: { key?: string | null } | null } | null;
  business?: {
    address?: { city?: string | null; state?: string | null } | null;
  } | null;
};

export type DealCardPreviewInput = {
  title?: string | null;
  primaryContact?: { name?: string | null } | null;
  isInboundListingBuyer?: boolean | null;
  inboundBuyerDisplay?: { listingTitle?: string | null } | null;
  listing?: DealCardListingShape | null;
  listingBrokerBuyerOpportunities?: Array<{
    listing?: DealCardListingShape | null;
  }> | null;
};

export type DealListingPreview = {
  title: string;
  imageKey: string | null;
  location: string | null;
  askingPrice: number | bigint | null;
};

const BUYER_REVIEW_STAGES = [
  "PREQUALIFIED",
  "LOI",
  "PURCHASE_AGREEMENT",
  "UNDER_CONTRACT",
];

export { formatRelativeTime as timeAgo } from "./relative-time";

export function formatCompactMoney(
  value: bigint | number | null | undefined,
): string | null {
  if (value == null) return null;
  const num = typeof value === "bigint" ? Number(value) : value;
  if (!Number.isFinite(num) || num <= 0) return null;
  if (num >= 1_000_000) {
    const millions = num / 1_000_000;
    const digits = millions >= 10 ? 0 : 1;
    const rounded = millions.toFixed(digits).replace(/\.0$/, "");
    return `$${rounded}M`;
  }
  if (num >= 1_000) return `$${Math.round(num / 1_000)}K`;
  return `$${Math.round(num).toLocaleString("en-US")}`;
}

/** "Ask $330K" — the price line on a pipeline card and an email tile. */
export function formatAskLabel(
  value: bigint | number | null | undefined,
): string | null {
  const money = formatCompactMoney(value);
  return money ? `Ask ${money}` : null;
}

function formatListingLocation(
  listing: DealCardListingShape | null | undefined,
) {
  const address = listing?.business?.address;
  if (!address) return null;
  return [address.city, address.state].filter(Boolean).join(", ") || null;
}

function listingImageKey(listing: DealCardListingShape | null | undefined) {
  return (
    listing?.listingMedia?.[0]?.image?.file?.key ??
    listing?.placeholderImage?.file?.key ??
    null
  );
}

/** Listing tile facts. Returns null when the title is just the contact's name. */
export function resolveDealListingPreview(
  deal: DealCardPreviewInput,
): DealListingPreview | null {
  const inboundListing = deal.listingBrokerBuyerOpportunities?.[0]?.listing;
  const listing = inboundListing ?? deal.listing ?? null;
  const title =
    listing?.pitch?.trim() ||
    deal.inboundBuyerDisplay?.listingTitle?.trim() ||
    listing?.title?.trim() ||
    null;
  if (!title) return null;
  const normalizedTitle = title.toLocaleLowerCase();
  const contactNames = [deal.primaryContact?.name?.trim(), deal.title?.trim()]
    .filter((value): value is string => Boolean(value))
    .map((value) => value.toLocaleLowerCase());
  if (!listing?.business && contactNames.includes(normalizedTitle)) {
    return null;
  }
  return {
    title,
    imageKey: listingImageKey(listing),
    location: formatListingLocation(listing),
    askingPrice: listing?.askingPrice ?? null,
  };
}

/** Whether the pipeline card (and the email) should show a listing tile. */
export function isDealListingPreviewVisible(input: {
  pipelineType?: string | null;
  stage: string;
  buyerKind: DealCardBuyerKind | null;
  hasSellerListing: boolean;
}): boolean {
  if (input.pipelineType === "BUYER") {
    return (
      input.buyerKind === "listing_inquiry" ||
      BUYER_REVIEW_STAGES.includes(input.stage)
    );
  }
  return input.hasSellerListing;
}

/** Actionable next step — never restates the board column label. */
export function buyerDealNextStep(
  stage: string,
  ctx: {
    unreadCount: number;
    pursuitCount: number | null;
    buyerKind: DealCardBuyerKind;
    engagementMode: "REPRESENTED_CLIENT" | "PRINCIPAL_BUYER" | null;
  },
): string | null {
  if (stage === "NEW_INQUIRY") {
    if (ctx.unreadCount > 0) return "Reply to qualify";
    return ctx.buyerKind === "listing_inquiry"
      ? "Respond about this listing"
      : "Follow up to qualify";
  }
  if (stage === "QUALIFIED" || stage === "NDA_SENT") {
    return "Get the Buyer NDA signed";
  }
  if (stage === "PREQUALIFICATION") return "Confirm buying capacity";
  if (stage === "PREQUALIFIED") {
    const isPrincipal = ctx.engagementMode === "PRINCIPAL_BUYER";
    if (isPrincipal) {
      if (ctx.pursuitCount && ctx.pursuitCount > 0) {
        return `Review ${ctx.pursuitCount} business${ctx.pursuitCount === 1 ? "" : "es"}`;
      }
      return "Find businesses to review";
    }
    if (!ctx.pursuitCount) return "Add listing suggestions";
    return "Waiting on client choices";
  }
  if (stage === "LOI") return "Advance the LOI";
  if (stage === "PURCHASE_AGREEMENT") return "Advance the purchase agreement";
  if (stage === "UNDER_CONTRACT") return "Drive to close";
  if (stage === "CLOSED_WON" || stage === "CLOSED_LOST") return null;
  return (
    BUYER_DEAL_STAGE_LABELS[stage as keyof typeof BUYER_DEAL_STAGE_LABELS] ??
    null
  );
}

export function sellerDealNextStep(stage: string): string | null {
  const actions: Record<string, string | null> = {
    LEAD: "Qualify the seller",
    QUALIFYING: "Finish seller intake",
    QUALIFIED: "Send the seller NDA",
    NDA_SENT: "Get the seller NDA signed",
    DISCOVERY: "Complete discovery",
    LISTING_AGREEMENT: "Finalize listing agreement",
    LISTING_PREP: "Finish listing materials",
    LIVE_ON_MARKET: "Field buyer interest",
    REVIEWING_OFFERS: "Compare offers",
    UNDER_CONTRACT: "Drive to close",
    CLOSED_WON: null,
    CLOSED_LOST: null,
  };
  return (
    actions[stage] ??
    SELLER_DEAL_STAGE_LABELS[stage as keyof typeof SELLER_DEAL_STAGE_LABELS] ??
    null
  );
}
